import {ChildPage, Page, PageGroup} from "./dto";
import {renderOEmbed} from "./oembed";

export const PAGE_IS_ABOUT_TO_CHANGE_EVENT = "page-is-about-to-change";

export const container = document.getElementById("container")!;
export const pageLangDropDown = document.getElementById("page-lang-dropdown")!;
export const pageLangButton = document.getElementById("page-lang-button")!;
export const pageTitle = document.getElementById("page-title")!;
export const pageText = document.getElementById("page-text")!;
export const pageLangList = document.getElementById("page-lang-list")!;
export const pageGroups = document.getElementById("page-groups")!;
export const pageChildren = document.getElementById("page-children")!;
export const pagePublicationDate = document.getElementById("page-publication-date")!;
export const pageAuthor = document.getElementById("page-author")!;
export const homeLink = document.getElementById("home-link")!;

export function currentPageLangList(): (string | null)[] {
	return Array.from(pageLangList.querySelectorAll("li")).map(elem =>
		elem.dataset.langCode !== undefined && elem.dataset.langCode !== "" ? elem.dataset.langCode : null
	);
}

export function currentPageGroups(): PageGroup[] {
	return Array.from(pageGroups.querySelectorAll("li")).map(elem => {
		return {
			id: parseInt(elem.dataset.id!),
			slug: elem.dataset.slug!,
			title: elem.querySelector("a")!.innerText,
			lang: elem.dataset.langCode !== "" ? elem.dataset.langCode! : null,
			pinned: elem.dataset.pinned !== "false",
			hidden: false
		};
	});
}

export function currentPageChildren(): ChildPage[] {
	return Array.from(pageChildren.children).map(elem => {
		const htmlElem = elem as HTMLElement;
		return {
			id: parseInt(htmlElem.dataset.id!),
			slug: htmlElem.dataset.slug!,
			title: (htmlElem.querySelector(".child-page-title a") as HTMLElement).innerText,
			text: htmlElem.querySelector(".child-page-text")!.innerHTML,
			lang: htmlElem.dataset.langCode !== "" ? htmlElem.dataset.langCode! : null,
			isGroup: htmlElem.dataset.isGroup !== "false",
			publishedAt: htmlElem.dataset.publishedAt !== "" ? htmlElem.dataset.publishedAt! : null,
			pinned: htmlElem.dataset.pinned !== "false",
			showAuthor: htmlElem.dataset.showAuthor !== "false",
			authorEmail: null,
			authorDisplayName: null,
			showPublishedAt: htmlElem.dataset.showPublishedAt !== "false",
			hasMore: htmlElem.dataset.hasMore !== "false"
		};
	});
}

export function currentPageId(): number {
	return parseInt(container.dataset.id!);
}

export function currentPageSlug(): string {
	return container.dataset.slug!;
}

export function currentPageLang(): string | null {
	return container.dataset.lang !== "" ? container.dataset.lang! : null;
}

export function currentPageIsGroup(): boolean {
	return container.dataset.isGroup !== "false";
}

export function currentPageIndex(): number {
	return parseInt(pageChildren.dataset.page!);
}

export function currentPagePublishedAt(): string | null {
	return container.dataset.publishedAt !== "" ? container.dataset.publishedAt! : null;
}

export function currentPageShowAuthor(): boolean {
	return container.dataset.showAuthor !== "false";
}

export function currentPageShowPublishedAt(): boolean {
	return container.dataset.showPublishedAt !== "false";
}

export function currentPage(): Page {
	return {
		id: currentPageId(),
		slug: currentPageSlug(),
		title: pageTitle.innerText,
		text: pageText.innerHTML,
		lang: currentPageLang(),
		availableLangList: currentPageLangList(),
		isGroup: currentPageIsGroup(),
		groups: currentPageGroups(),
		children: currentPageChildren(),
		page: currentPageIndex(),
		hasMore: pageChildren.dataset.hasMore! !== "false",
		publishedAt: currentPagePublishedAt(),
		showAuthor: currentPageShowAuthor(),
		authorEmail: null,
		authorDisplayName: null,
		showPublishedAt: currentPageShowPublishedAt()
	};
}

const getFlagEmoji = (countryCode: string) => {
	const codePoints = (countryCode !== "en" ? countryCode : "us")
		.toUpperCase()
		.split("")
		.map(char =>  127397 + char.charCodeAt(0));
	return String.fromCodePoint(...codePoints);
}

export function renderPage(page: Page) {
	if (page.id !== currentPageId()) {
		document.dispatchEvent(new CustomEvent(PAGE_IS_ABOUT_TO_CHANGE_EVENT, {detail: page}));
	}
	if (page.lang !== null) {
		document.documentElement.setAttribute("lang", page.lang);
	} else {
		document.documentElement.removeAttribute("lang");
	}
	document.title = page.title;
	container.dataset.id = page.id.toString();
	container.dataset.slug = page.slug;
	container.dataset.lang = page.lang !== null ? page.lang : "";
	if (page.lang !== null) {
		pageLangButton.innerText = getFlagEmoji(page.lang);
	} else {
		pageLangButton.innerHTML = "🌎";
	}
	container.dataset.isGroup = page.isGroup ? "true" : "false";
	container.dataset.publishedAt = page.publishedAt !== null ? page.publishedAt : "";
	container.dataset.showAuthor = page.showAuthor ? "true" : "false";
	container.dataset.showPublishedAt = page.showPublishedAt ? "true" : "false";
	pageTitle.innerText = page.title;
	pageText.innerHTML = page.text;
	pageLangDropDown.style.display = page.availableLangList.length <= 1 ? "none" : "";
	pageLangButton.title = localeStr("language");
	pageLangList.innerHTML = "";
	for (let lang of page.availableLangList) {
		if (lang === page.lang) {
			continue;
		}
		const elem = document.createElement("li");
		elem.dataset.langCode = lang !== null ? lang : "";
		if (lang !== null) {
			elem.innerHTML = `<a href="/${lang}/${page.slug}"><button class="top-button">${getFlagEmoji(lang)}</button></a>`;
		} else {
			elem.innerHTML = `<a href="/${page.slug}"><button class="top-button">🌎</button></a>`;
		}
		pageLangList.appendChild(elem);
	}
	pageGroups.innerHTML = "";
	for (let group of page.groups) {
		const elem = document.createElement("li");
		elem.dataset.id = group.id.toString();
		elem.dataset.slug = group.slug;
		elem.dataset.langCode = group.lang !== null ? group.lang : "";
		elem.dataset.pinned = group.pinned ? "true" : "false";
		const linkElem = document.createElement("a");
		linkElem.href = makePageUrl(group);
		linkElem.innerText = group.title;
		elem.appendChild(linkElem);
		pageGroups.appendChild(elem);
	}
	pageAuthor.innerHTML = "";
	if (page.showAuthor && page.authorDisplayName !== null) {
		if (page.authorEmail !== null) {
			const linkElem = document.createElement("a");
			linkElem.href = `mailto:${page.authorEmail}`;
			linkElem.innerText = page.authorDisplayName;
			pageAuthor.appendChild(linkElem);
		} else {
			pageAuthor.innerText = page.authorDisplayName;
		}
	}
	pageChildren.innerHTML = "";
	for (let child of page.children) {
		const elem = document.createElement("div");
		elem.className = "child-page";
		elem.dataset.id = child.id.toString();
		elem.dataset.slug = child.slug;
		elem.dataset.langCode = child.lang !== null ? child.lang : "";
		elem.dataset.isGroup = child.isGroup ? "true" : "false";
		elem.dataset.publishedAt = child.publishedAt !== null ? child.publishedAt : "";
		elem.dataset.pinned = child.pinned ? "true" : "false";
		elem.dataset.showAuthor = child.showAuthor ? "true" : "false";
		elem.dataset.showPublishedAt = child.showPublishedAt ? "true" : "false";
		elem.dataset.hasMore = child.hasMore ? "true" : "false";
		const titleElem = document.createElement("h2");
		titleElem.className = "child-page-title";
		const titleLink = document.createElement("a");
		titleLink.href = makePageUrl(child);
		titleLink.innerText = child.title;
		titleElem.appendChild(titleLink);
		if (child.isGroup) {
			titleElem.append(" 🖿");
		}
		if (child.pinned) {
			titleElem.append(" 📌");
		}
		elem.appendChild(titleElem);
		const textElem = document.createElement("div");
		textElem.className = "child-page-text";
		textElem.innerHTML = child.text;
		elem.appendChild(textElem);
		if (child.hasMore) {
			const readMoreElem = document.createElement("div");
			readMoreElem.className = "child-page-read-more";
			const linkElem = document.createElement("a");
			linkElem.href = makePageUrl(child);
			linkElem.innerText = localeStr("readMore");
			readMoreElem.appendChild(linkElem);
			elem.appendChild(readMoreElem);
		}
		const footerElem = document.createElement("div");
		footerElem.className = "child-page-footer";
		const authorElem = document.createElement("div");
		authorElem.className = "child-page-author";
		if (child.showAuthor && child.authorDisplayName !== null) {
			if (child.authorEmail !== null) {
				const linkElem = document.createElement("a");
				linkElem.href = `mailto:${child.authorEmail}`;
				linkElem.innerText = child.authorDisplayName;
				authorElem.appendChild(linkElem);
			} else {
				authorElem.innerText = child.authorDisplayName;
			}
		}
		footerElem.appendChild(authorElem);
		const publicationDateElem = document.createElement("div");
		publicationDateElem.className = "child-page-publication-date";
		footerElem.append(publicationDateElem);
		elem.appendChild(footerElem);
		pageChildren.appendChild(elem);
	}
	pageChildren.dataset.page = page.page.toString();
	pageChildren.dataset.hasMore = page.hasMore ? "true" : "false";
	Array.from(document.getElementsByClassName("page-group-nav")).forEach(pageGroupNav => {
		pageGroupNav.innerHTML = "";
		if (page.page > 0) {
			const elem = document.createElement("a");
			elem.href = `?page=${page.page - 1}`;
			elem.style.float = "left";
			elem.innerText = localeStr("prevPage");
			pageGroupNav.appendChild(elem);
		}
		if (page.hasMore) {
			const elem = document.createElement("a");
			elem.href = `?page=${page.page + 1}`;
			elem.style.float = "right";
			elem.innerText = localeStr("nextPage");
			pageGroupNav.appendChild(elem);
		}
	});
	if (page.slug !== "") {
		const url = page.lang !== null ? `/${page.lang}/` : "/";
		homeLink.innerHTML = `<a href="${url}">${localeStr("home")}</a>`;
	} else {
		homeLink.innerHTML = "";
	}
	renderPagePublicationDate();
	if (window.hljs !== undefined) {
		window.hljs.highlightAll();
	}
	renderOEmbed();
}

export function makePageUrl(page: Page | PageGroup | ChildPage, hash?: string) {
	let url = page.lang !== null ? `/${page.lang}/${page.slug}` : `/${page.slug}`;
	if ("page" in page && page.page > 0) {
		url += `?page=${page.page}`;
	}
	if (hash !== undefined) {
		url += hash;
	}
	return url;
}

export function replaceCurrentPage(page: Page, hash?: string) {
	history.replaceState(
		"navigation" in window ? null : page,
		page.title,
		makePageUrl(page, hash)
	);
	renderPage(page);
}

export function pushCurrentPage(page: Page, hash?: string) {
	history.pushState(
		"navigation" in window ? null : page,
		page.title,
		makePageUrl(page, hash)
	);
	renderPage(page);
}

export function renderPagePublicationDate() {
	if (!currentPageIsGroup()) {
		const publishedAt = currentPagePublishedAt();
		if (publishedAt !== null && currentPageShowPublishedAt()) {
			const date = new Date(publishedAt);
			pagePublicationDate.innerText = date.toLocaleString();
		} else {
			pagePublicationDate.innerText = "";
		}
	} else {
		pagePublicationDate.innerText = "";
		Array.from(pageChildren.children).forEach(childPageElem => {
			const childPageHtmlElem = childPageElem as HTMLElement;
			Array.from(childPageHtmlElem.getElementsByClassName("child-page-publication-date")).forEach(
				dateElem => {
					const dateHtmlElem = dateElem as HTMLElement;
					if (
						childPageHtmlElem.dataset.showPublishedAt !== "false" &&
						childPageHtmlElem.dataset.publishedAt !== ""
					) {
						const date = new Date(childPageHtmlElem.dataset.publishedAt!);
						dateHtmlElem.innerText = date.toLocaleString();
					} else {
						dateHtmlElem.innerText = "";
					}
				}
			);
		});
	}
}

const localesLoadCallbacks: (() => void)[] = [];
if (window.APP_LOCALES === undefined) {
	window.APP_LOCALES = {};
	fetch((document.getElementById("locales-json") as HTMLInputElement).value)
		.then(response => {
			if (response.status < 200 || response.status >= 299) {
				console.error("Unable to load locales", response);
				return;
			}
			return response.json();
		})
		.then(response => {
			window.APP_LOCALES = response;
			localesLoadCallbacks.splice(0, localesLoadCallbacks.length).forEach(callback => callback());
		});
}

export function waitLocalesLoaded(callback: () => void) {
	if (Object.keys(window.APP_LOCALES!).length === 0) {
		localesLoadCallbacks.push(callback);
	} else {
		callback();
	}
}

export function localeStr(key: string): string {
	if (window.APP_LOCALES !== undefined) {
		// TODO: Use user lang if page lang is missing
		const localeCode = currentPageLang() || "";
		// TODO: Use fallback lang instead of English
		const locale = window.APP_LOCALES[localeCode] || window.APP_LOCALES["en"];
		const value = locale[key];
		if (value !== undefined) {
			return value;
		}
	}
	return key;
}
